import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/ui/dialog';
import { Button } from 'src/ui/button';
import type { Colors } from 'src/ui/types';

interface Props {
  title?: string;
  description?: string | React.ReactNode;
  buttonCancelText?: string;
  buttonOkText?: string;
  /** e.g. 'error' for a destructive confirm. Unset keeps the default color. */
  buttonOkColor?: Colors;
  open: boolean;
  handleClose: () => void;
  handleOk: () => void;
  hasActions?: boolean;
  /** Outlined Cancel color. Defaults to primary so existing confirms stay unchanged. */
  cancelColor?: Colors;
}

/** Body for a delete confirmation. Names the item and says the action can't be undone. */
export const deleteConfirmDescription = (name: string) =>
  `Are you sure you want to delete "${name}"? This can't be undone.`;

export const ConfirmDialog: React.FC<Props> = ({
  title = '',
  description = '',
  buttonCancelText = 'Cancel',
  buttonOkText = 'OK',
  buttonOkColor,
  open,
  handleClose,
  handleOk,
  hasActions = true,
  cancelColor = 'primary',
}) => {
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Comprehensive cleanup for pointer events and dialog state
  const performCleanup = () => {
    // Clear any existing timeout
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
    }

    // Immediate cleanup
    document.body.style.pointerEvents = '';
    document.documentElement.style.pointerEvents = '';
    document.body.style.overflow = '';

    // Remove any potential modal-related classes or attributes
    document.body.removeAttribute('data-scroll-locked');
    document.body.classList.remove('overflow-hidden');

    // Force a reflow to ensure changes take effect
    document.body.offsetHeight;
  };

  // Enhanced cleanup when dialog closes
  useEffect(() => {
    if (!open) {
      // Immediate cleanup
      performCleanup();

      // Additional cleanup after animation completes
      cleanupTimeoutRef.current = setTimeout(() => {
        performCleanup();

        // Check for any remaining Radix UI portal elements that might be blocking interactions
        const portals = document.querySelectorAll('[data-radix-portal]');
        portals.forEach((portal) => {
          const overlay = portal.querySelector('[data-state="closed"]');
          if (overlay) {
            // Ensure closed overlays don't block pointer events
            (overlay as HTMLElement).style.pointerEvents = 'none';
          }
        });
      }, 600); // Longer timeout to ensure all animations complete

      return () => {
        if (cleanupTimeoutRef.current) {
          clearTimeout(cleanupTimeoutRef.current);
        }
      };
    }
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
      performCleanup();
    };
  }, []);

  const handleCloseWithCleanup = () => {
    handleClose();
  };

  const handleCloseWithEventCleanup = (event?: React.MouseEvent) => {
    // Prevent event bubbling
    if (event) {
      event.stopPropagation();
    }
    handleClose();
  };

  const handleOkWithCleanup = (event?: React.MouseEvent) => {
    // Prevent event bubbling
    if (event) {
      event.stopPropagation();
    }
    handleOk();
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleCloseWithCleanup}>
      <DialogContent
        closeIcon
        onCloseClick={(event) => event.stopPropagation()}
        className="sm:max-w-md"
        omitTitle
        omitDescription={Boolean(description)}
        onPointerDownOutside={handleCloseWithCleanup}
        onEscapeKeyDown={handleCloseWithCleanup}
      >
        <DialogHeader>
          <DialogTitle className="pr-8">{title}</DialogTitle>
          {description && (
            <DialogDescription className="text-sm text-muted-foreground">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        {hasActions && (
          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
            <Button variant="outlined" color={cancelColor} onClick={handleCloseWithEventCleanup}>
              {buttonCancelText}
            </Button>
            <Button variant="contained" color={buttonOkColor} onClick={handleOkWithCleanup}>
              {buttonOkText}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
